/** @jsxImportSource @emotion/react */
import React, { useCallback, useEffect, useRef } from 'react'
import css from '@emotion/css/macro'
import { InputRef } from 'antd'
import type { BaseSelectRef } from 'rc-select'
import {
  DeleteOutlined,
  PlusCircleOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import { PRIMARY_BUTTON_CONTENT } from '@sportninja/common/constants/buttonColors'
import { Image, Upload } from 'antd'
import Text from 'src/components/ts-components/Text'
import { TextInput } from 'src/components/ts-components/TextInput'
import { SelectInput } from 'src/components/ts-components/SelectInput'
import { SwitchInput } from 'src/components/ts-components/SwitchInput'
import CopyComponent from 'src/components/CopyComponent'
import DeleteCompetitionModal from 'src/components/Modal/DeleteCompetitionModal'
import { useDeleteModal } from 'src/hooks/useDeleteModal'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import { resolveAssetUrl } from '@sportninja/common/utils/assetUrl'
import req from '@sportninja/common/api/request'
import { useTeamContext } from '../../TeamContext'
import { weightSemiBold } from 'src/components/css'
import { paragraphSmallStyle } from 'src/pages/RegistrationInviteV2/steps/Main'

const FIELD_ORDER = ['name_full', 'sport_id', 'sf_id'] as const

const Separator = () => {
  return (
    <div
      css={css`
        border-bottom: 1px solid ${colors.SOFT_STEEL};
        width: 100%;
      `}
    />
  )
}

const DeleteButton = ({ ...props }) => {
  return (
    <button
      css={css`
        display: flex;
        padding: 8px 16px 8px 8px;
        justify-content: center;
        align-items: center;
        gap: 8px;
        border-radius: 8px;
        border: 1px solid ${colors.ERROR_200};
        background: ${colors.ERROR_200};
        box-shadow: 0px 2px 0px 0px rgba(5, 145, 255, 0.1);
        cursor: pointer;
        &:hover {
          opacity: 0.9;
        }
      `}
      {...props}
    >
      <DeleteOutlined
        css={css`
          font-size: 24px;
          color: ${colors.NEUTRAL_0};
        `}
        width={24}
        height={24}
      />
      <Text variant='heading6' weight='semiBold' color={colors.NEUTRAL_0}>
        DELETE TEAM
      </Text>
    </button>
  )
}

interface Props {
  isMobile?: boolean
}

const GeneralSettings = ({ isMobile }: Props) => {
  const {
    state,
    dispatch,
    isEdit,
    beforeUpload,
    handleChange,
    handleChangeBackground,
    sportOptions,
    genericId,
    onDeleteSuccess,
    showSuccessMessage,
    permission,
  } = useTeamContext()

  const nameRef = useRef<InputRef>(null)
  const sportRef = useRef<BaseSelectRef>(null)
  const sfIdRef = useRef<InputRef>(null)

  useEffect(() => {
    if (state.fieldErrors.length > 0) {
      const fieldRefs: Record<
        string,
        React.RefObject<{ focus?: () => void } | null>
      > = {
        name_full: nameRef,
        sport_id: sportRef,
        sf_id: sfIdRef,
      }
      const firstErrorField = FIELD_ORDER.find((field) =>
        state.fieldErrors.includes(field)
      )
      if (firstErrorField) {
        fieldRefs[firstErrorField]?.current?.focus?.()
      }
    }
  }, [state.fieldErrors])

  const deleteModal = useDeleteModal()

  // Build grouped select options following ScheduleModal pattern
  const selectOptions = [
    {
      label: (
        <div
          css={css`
            display: flex;
            flex-direction: row;
            align-items: center;
            justify-content: space-between;
          `}
        >
          <span
            css={css`
              ${paragraphSmallStyle}
              ${weightSemiBold}
              color: ${colors.PRIMARY};
              text-transform: uppercase;
            `}
          >
            Full Stats
          </span>
          <QuestionCircleOutlined
            style={{
              fontSize: 16,
              color: colors.PRIMARY,
              cursor: 'pointer',
            }}
            onClick={() => {
              window.open(
                'https://support.sportninja.com/scoring-2-versions-basic-vs-full-stats',
                '_blank'
              )
            }}
          />
        </div>
      ),
      title: 'fullStats',
      options: sportOptions
        ?.filter((s) => !s.is_generic)
        ?.map((option) => ({
          value: option.id,
          label: option.name_full || option.name,
          is_generic: false,
        }))
        ?.sort((a, b) => a?.label?.localeCompare(b?.label)),
    },
    {
      label: (
        <div
          css={css`
            display: flex;
            flex-direction: row;
            align-items: center;
            justify-content: space-between;
          `}
        >
          <span
            css={css`
              ${paragraphSmallStyle}
              ${weightSemiBold}
              color: ${colors.PRIMARY};
              text-transform: uppercase;
            `}
          >
            Basic Stats
          </span>
          <QuestionCircleOutlined
            style={{
              fontSize: 16,
              color: colors.PRIMARY,
              cursor: 'pointer',
            }}
            onClick={() => {
              window.open(
                'https://support.sportninja.com/scoring-2-versions-basic-vs-full-stats',
                '_blank'
              )
            }}
          />
        </div>
      ),
      title: 'basicStats',
      options: sportOptions
        ?.filter((s) => s.is_generic)
        ?.map((option) => ({
          value: option.id,
          label: option.name,
          is_generic: true,
        }))
        ?.sort((a, b) => a?.label?.localeCompare(b?.label)),
    },
  ]

  // Filter function for grouped select options
  const filterOptionGroup = useCallback(
    (search: string, optionOrGroup: { options?: unknown[]; label: string }) => {
      const isGroup = Array.isArray(optionOrGroup.options)
      if (isGroup) {
        return false
      }
      return optionOrGroup.label.toLowerCase().includes(search.toLowerCase())
    },
    []
  )

  // Handle sport selection
  const handleSportChange = useCallback(
    (value: string) => {
      const selectedSportFromSelect = sportOptions?.find((s) => s.id === value)
      if (selectedSportFromSelect?.is_generic) {
        dispatch({
          type: 'SET_SPORT',
          payload: {
            sportId: genericId,
            genericSportId: selectedSportFromSelect.id,
            selectedSport: value,
          },
        })
      } else {
        dispatch({
          type: 'SET_SPORT',
          payload: {
            sportId: selectedSportFromSelect?.id || null,
            genericSportId: null,
            selectedSport: value,
          },
        })
      }
    },
    [sportOptions, genericId, dispatch]
  )

  return (
    <div
      css={css`
        padding: 16px;
        display: flex;
        flex-direction: column;
        overflow-y: auto;
        height: 100%;
        gap: 16px;
        ${isMobile &&
        css`
          padding-left: 8px;
          padding-right: 8px;
          width: 100%;
        `}
        .ant-upload-wrapper .ant-upload-select {
          width: 100% !important;
        }
      `}
    >
      {/* Header with ID copy */}
      <div
        css={css`
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
          align-self: stretch;
        `}
      >
        <Text variant='captionLarge' weight='semiBold'>
          General Info
        </Text>
        {isEdit && state.id ? (
          <div>
            <CopyComponent id={state.id} />
          </div>
        ) : null}
      </div>

      {/* Logo Upload */}
      {state.image_url ? (
        <div
          css={css`
            display: flex;
            padding: 8px;
            align-items: center;
            gap: 8px;
            align-self: stretch;
            border-radius: 8px;
            background-color: ${colors.SECONDARY_200};
            border: 1px solid ${colors.SECONDARY_100};
            width: 100%;
          `}
        >
          <div
            css={css`
              display: flex;
              align-items: center;
              justify-content: space-between;
              gap: 8px;
              flex: 1 0 0;
            `}
          >
            <Image
              src={resolveAssetUrl(state.image_url)}
              alt='team logo'
              width={56}
              height={56}
              css={css`
                border-radius: 4px;
                object-fit: cover;
              `}
              preview={false}
              onError={(e) => {
                e.currentTarget.src =
                  '/images/generic_placeholders/placeholder_image.png'
              }}
            />
            <DeleteOutlined
              css={css`
                color: ${colors.ERROR_200};
                font-size: 24px;
                cursor: pointer;
              `}
              onClick={() => {
                dispatch({
                  type: 'SET_IMAGE',
                  payload: { url: null, data: null, changed: true },
                })
              }}
            />
          </div>
        </div>
      ) : (
        <Upload
          beforeUpload={beforeUpload}
          onChange={handleChange}
          showUploadList={false}
          customRequest={({ onSuccess }) => onSuccess?.('ok' as any)}
          css={css`
            .ant-upload-wrapper .ant-upload {
              width: 100% !important;
            }
            width: 100%;
          `}
        >
          <div
            css={css`
              display: flex;
              padding: 8px;
              align-items: center;
              gap: 8px;
              align-self: stretch;
              border-radius: 8px;
              border: 1px dashed ${colors.SECONDARY_100};
              cursor: pointer;
              width: 100%;
            `}
          >
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: 8px;
                flex: 1 0 0;
              `}
            >
              <div
                css={css`
                  display: flex;
                  width: 48px;
                  height: 48px;
                  flex-direction: column;
                  justify-content: center;
                  align-items: center;
                  border-radius: 4px;
                  background: ${colors.PRIMARY};
                `}
              >
                <div
                  css={css`
                    display: flex;
                    width: 24px;
                    height: 24px;
                    flex-direction: column;
                    justify-content: center;
                    align-items: center;
                    border-radius: 4px;
                    background: ${colors.WHITE};
                  `}
                >
                  <PlusCircleOutlined
                    css={css`
                      font-size: 24px;
                      color: ${PRIMARY_BUTTON_CONTENT.icon};
                      background-color: ${colors.PRIMARY};
                    `}
                  />
                </div>
              </div>
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  justify-content: center;
                  align-items: flex-start;
                `}
              >
                <Text
                  variant='paragraphSmall'
                  weight='regular'
                  color={colors.NEUTRAL_0}
                >
                  Team Logo
                </Text>
                <Text
                  variant='paragraphSmall'
                  weight='medium'
                  color={colors.PRIMARY}
                >
                  Click to Upload
                </Text>
              </div>
            </div>
          </div>
        </Upload>
      )}

      {/* Team Name */}
      <div
        css={css`
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 8px;
          width: 100%;
        `}
      >
        <TextInput
          ref={nameRef}
          name='name_full'
          value={state.name_full}
          setValue={(value) => {
            dispatch({ type: 'SET_FIELD', field: 'name_full', value })
          }}
          fieldErrors={state.fieldErrors}
          customError={state.customError}
          label='Team Name *'
        />
      </div>

      {/* Sport Type */}
      <div
        css={css`
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 8px;
          width: 100%;
        `}
      >
        <SelectInput
          inputRef={sportRef}
          placeholder='Select Sport'
          label='Sport *'
          disabled={isEdit}
          options={selectOptions}
          filterOption={filterOptionGroup}
          name='sport_id'
          value={state.selectedSport || ''}
          showSearch={true}
          onChange={handleSportChange}
          fieldErrors={state.fieldErrors}
          customError={state.customError}
        />
      </div>

      <Separator />

      {/* Customization - Background Image */}
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 8px;
          align-self: stretch;
        `}
      >
        <Text
          variant='captionSmall'
          weight='semiBold'
          color={colors.NEUTRAL_50}
        >
          Customization
        </Text>
      </div>

      {state.background_image_url ? (
        <div
          css={css`
            display: flex;
            padding: 8px;
            align-items: center;
            gap: 8px;
            align-self: stretch;
            border-radius: 8px;
            background-color: ${colors.SECONDARY_200};
            border: 1px solid ${colors.SECONDARY_100};
            width: 100%;
          `}
        >
          <div
            css={css`
              display: flex;
              align-items: center;
              justify-content: space-between;
              gap: 8px;
              flex: 1 0 0;
            `}
          >
            <Image
              src={resolveAssetUrl(state.background_image_url)}
              alt='background'
              width={160}
              height={48}
              css={css`
                border-radius: 4px;
                object-fit: cover;
              `}
              preview={false}
              onError={(e) => {
                e.currentTarget.src =
                  '/images/generic_placeholders/placeholder_image.png'
              }}
            />
            <DeleteOutlined
              css={css`
                color: ${colors.ERROR_200};
                font-size: 24px;
                cursor: pointer;
              `}
              onClick={() => {
                dispatch({
                  type: 'SET_BACKGROUND_IMAGE',
                  payload: { url: null, data: null, changed: true },
                })
              }}
            />
          </div>
        </div>
      ) : (
        <Upload
          beforeUpload={beforeUpload}
          onChange={handleChangeBackground}
          showUploadList={false}
          customRequest={({ onSuccess }) => onSuccess?.('ok' as any)}
          css={css`
            .ant-upload-wrapper .ant-upload {
              width: 100% !important;
            }
            width: 100%;
          `}
        >
          <div
            css={css`
              display: flex;
              padding: 8px;
              align-items: center;
              gap: 8px;
              align-self: stretch;
              border-radius: 8px;
              border: 1px dashed ${colors.SECONDARY_100};
              cursor: pointer;
            `}
          >
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: 8px;
                flex: 1 0 0;
              `}
            >
              <div
                css={css`
                  display: flex;
                  width: 48px;
                  height: 48px;
                  flex-direction: column;
                  justify-content: center;
                  align-items: center;
                  border-radius: 4px;
                  background: ${colors.PRIMARY};
                `}
              >
                <div
                  css={css`
                    display: flex;
                    width: 24px;
                    height: 24px;
                    flex-direction: column;
                    justify-content: center;
                    align-items: center;
                    border-radius: 4px;
                    background: ${colors.WHITE};
                  `}
                >
                  <PlusCircleOutlined
                    css={css`
                      font-size: 24px;
                      color: ${PRIMARY_BUTTON_CONTENT.icon};
                      background-color: ${colors.PRIMARY};
                    `}
                  />
                </div>
              </div>
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  justify-content: center;
                  align-items: flex-start;
                `}
              >
                <div
                  css={css`
                    display: flex;
                    align-items: center;
                    gap: 4px;
                  `}
                >
                  <Text
                    variant='paragraphSmall'
                    weight='regular'
                    color={colors.NEUTRAL_0}
                  >
                    Header Background
                  </Text>
                  <Text
                    variant='paragraphXSmall'
                    weight='medium'
                    color={colors.NEUTRAL_100}
                  >
                    (Recommended: 1440x300)
                  </Text>
                </div>
                <Text
                  variant='paragraphSmall'
                  weight='medium'
                  color={colors.PRIMARY}
                >
                  Click to Upload
                </Text>
              </div>
            </div>
          </div>
        </Upload>
      )}

      <Separator />

      {/* Placeholder Checkbox - only show if permission.delete */}
      {permission?.delete && (
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 24px;
            align-self: stretch;
          `}
        >
          <SwitchInput
            title='Placeholder Team'
            subtitle='Placeholder Teams Are Used As Temporary Slots That Can Be Replaced Later.'
            value={state.is_placeholder}
            onChange={(value) => {
              dispatch({ type: 'SET_FIELD', field: 'is_placeholder', value })
            }}
          />
        </div>
      )}

      {/* Salesforce ID - Canlan only */}
      {isCanlan && (
        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
            flex: 1 0 0;
            width: 100%;
          `}
        >
          <TextInput
            ref={sfIdRef}
            name='sf_id'
            value={state.sf_id}
            setValue={(value) => {
              dispatch({ type: 'SET_FIELD', field: 'sf_id', value })
            }}
            fieldErrors={state.fieldErrors}
            customError={state.customError}
            label='Salesforce ID'
          />
        </div>
      )}

      {/* Danger Zone - edit mode, and only where deletion is permitted. Callers
          that manage deletion elsewhere (the Operations tab has its own delete
          on the tree row) pass permission.delete === false to hide it. */}
      {isEdit && permission?.delete !== false && (
        <>
          <Separator />
          <div
            css={css`
              display: flex;
              flex-direction: column;
              align-items: flex-start;
              gap: 8px;
              flex: 1 0 0;
              width: 100%;
            `}
          >
            <Text
              variant='captionSmall'
              weight='semiBold'
              color={colors.NEUTRAL_50}
            >
              Danger Zone
            </Text>
            <Text
              variant='paragraphXSmall'
              weight='regular'
              color={colors.WHITE}
            >
              Permanently Delete The Team And All Info Related.
            </Text>
            <DeleteButton onClick={deleteModal.open} />
          </div>
        </>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteCompetitionModal
        isOpen={deleteModal.isOpen}
        onClose={deleteModal.close}
        onConfirm={async (shouldForce = false) => {
          const query: Record<string, number> = {}
          if (shouldForce) {
            query.force = 1
          }
          await req(`/teams/${state.id}`, {
            method: 'DELETE',
            query,
          })
          showSuccessMessage(`${state.name_full} deleted successfully`)
          onDeleteSuccess()
        }}
        title='Delete Team'
        message={'Are You Sure You Want To Delete This Team?'}
        itemName={state.name_full}
        buttonText='Delete'
        useForceDelete={true}
        displayBanner={false}
        bannerText=''
      />
    </div>
  )
}

export default GeneralSettings
